import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from './useAuth';

export interface ToolReport {
  id: string;
  toolName: string;
  issueType: string;
  description: string | null;
  status: string;
  reporterId: string;
  reporterName: string;
  osId: string | null;
  osNumber: number | null;
  resolvedBy: string | null;
  resolverName: string | null;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  createdAt: string;
}

interface ToolReportFilters {
  status?: 'open' | 'acknowledged' | 'resolved';
}

export function useToolReports(filters: ToolReportFilters = {}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const wid = user?.workshopId;

  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();

  const reportsQuery = useQuery<{ reports: ToolReport[] }>({
    queryKey: ['tool-reports', wid, filters],
    queryFn: async () => {
      const res = await api.get(
        `/workshops/${wid}/tool-reports${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
    enabled: !!wid,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.patch(`/workshops/${wid}/tool-reports/${id}`, {
        status: 'acknowledged',
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tool-reports'] });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async ({
      id,
      resolutionNotes,
    }: {
      id: string;
      resolutionNotes?: string;
    }) => {
      const res = await api.patch(`/workshops/${wid}/tool-reports/${id}`, {
        status: 'resolved',
        resolutionNotes,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tool-reports'] });
    },
  });

  return {
    reports: reportsQuery.data?.reports ?? [],
    isLoading: reportsQuery.isLoading,
    error: reportsQuery.error,
    isError: reportsQuery.isError,
    refetch: reportsQuery.refetch,
    acknowledge: acknowledgeMutation.mutateAsync,
    isAcknowledging: acknowledgeMutation.isPending,
    resolve: resolveMutation.mutateAsync,
    isResolving: resolveMutation.isPending,
  };
}
